import { useMemo } from 'react';
import useFindOrdersQuery from '@/api/queries/useFindOrdersQuery';
import useOrderAttentionItemsQuery from '@/api/queries/useOrderAttentionItemsQuery';
import useOrderMetricsQuery from '@/api/queries/useOrderMetricsQuery';
import useStoreMetricsQuery from '@/api/queries/useStoreMetricsQuery';
import useStoreOrderVariancesQuery from '@/api/queries/useStoreOrderVariancesQuery';
import useSupplierStoreOrderVariancesQuery from '@/api/queries/useSupplierStoreOrderVariancesQuery';
import Button from '@/components/Button';
import LatestOrdersStats from '@/components/dashboard/LatestOrdersStats';
import NeedsAttentionStats from '@/components/dashboard/NeedsAttentionStats';
import OverdueStoresStats from '@/components/dashboard/OverdueStoresStats';
import StatCard from '@/components/dashboard/StatCard';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { OrderListPath } from '@/modules/order/paths';
import { SupplierAttentionPath } from '@/modules/supplier/paths';
import {
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';

const LAST_ORDERS_LIMIT = 5;
const ESTIMATED_ATTENTION_COUNT_LIMIT = 10;

function getWeekStart(date: Date) {
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() + diff);
  return start;
}

const formatCount = (count: number, estimated?: boolean) =>
  `${count.toLocaleString()}${estimated ? '+' : ''}`;

const Home = () => {
  const auth = useAuth();
  const supplierId = auth.currentUser().supplierId;
  const supplierFilters = useMemo(() => (supplierId ? { supplierId } : undefined), [supplierId]);
  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => getWeekStart(today), [today]);

  const { isLoading: isLoadingOrdersThisWeek, totalOrderCount: ordersThisWeekCount } =
    useOrderMetricsQuery(weekStart, today, undefined, supplierFilters);

  const { activeStoreCount, isLoading: isLoadingStoreMetrics } =
    useStoreMetricsQuery(supplierFilters);

  const {
    items: attentionItems,
    totalCount: attentionCount,
    isEstimatedTotalCount: isEstimatedAttentionCount,
    isLoading: isLoadingAttention,
    resolveIncompleteDelivery,
    isResolvingIncompleteDelivery,
  } = useOrderAttentionItemsQuery(supplierFilters, LAST_ORDERS_LIMIT);

  const { orders, isLoading: isLoadingOrders } = useFindOrdersQuery(1, undefined, supplierFilters);

  const latestOrderIds = useMemo(
    () =>
      [...orders.items]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, LAST_ORDERS_LIMIT)
        .map((order) => order.id),
    [orders.items],
  );

  const {
    variances: overdueStores,
    totalCount: overdueStoreCount,
    isEstimatedTotalCount: isEstimatedOverdueStoreCount,
    isLoading: isLoadingOverdueStores,
  } = useSupplierStoreOrderVariancesQuery(supplierFilters, LAST_ORDERS_LIMIT);

  const { variancesByOrderId } = useStoreOrderVariancesQuery(latestOrderIds, supplierFilters);

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" />

      <section className="space-y-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <StatCard
            title="Needs your attention"
            value={formatCount(attentionCount, isEstimatedAttentionCount)}
            loading={isLoadingAttention}
            className="border-red-300"
            titleClassName="text-red-700"
            valueClassName="text-red-700"
            icon={<ExclamationTriangleIcon className="text-red-600" />}
            size="lg"
          />

          <StatCard
            title="Orders this week"
            value={ordersThisWeekCount.toLocaleString()}
            loading={isLoadingOrdersThisWeek}
            className="border-transparent shadow-none"
            icon={<ShoppingBagIcon className="text-gray-400" />}
            size="lg"
          />

          <StatCard
            title="Overdue stores"
            value={formatCount(overdueStoreCount, isEstimatedOverdueStoreCount)}
            loading={isLoadingOverdueStores}
            className="border-transparent shadow-none"
            icon={<CalendarDaysIcon className="text-gray-400" />}
            size="lg"
          />

          <StatCard
            title="Active customer stores"
            value={activeStoreCount.toLocaleString()}
            loading={isLoadingStoreMetrics}
            className="border-transparent shadow-none"
            icon={<BuildingStorefrontIcon className="text-gray-400" />}
            size="lg"
          />
        </div>

        <NeedsAttentionStats
          items={attentionItems}
          totalCount={attentionCount}
          isEstimatedTotalCount={isEstimatedAttentionCount}
          isLoading={isLoadingAttention}
          isResolving={isResolvingIncompleteDelivery}
          countDisplayLimit={LAST_ORDERS_LIMIT}
          estimatedCountDisplayLimit={ESTIMATED_ATTENTION_COUNT_LIMIT}
          viewAllPath={SupplierAttentionPath}
          onResolveIncompleteDelivery={(orderId, incompleteDeliveryId) =>
            resolveIncompleteDelivery({ orderId, incompleteDeliveryId })
          }
        />

        <OverdueStoresStats
          variances={overdueStores}
          isLoading={isLoadingOverdueStores}
          limit={LAST_ORDERS_LIMIT}
          totalCount={overdueStoreCount}
          isEstimatedTotalCount={isEstimatedOverdueStoreCount}
        />

        <div>
          <div className="mb-3 flex items-center justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Latest {LAST_ORDERS_LIMIT} orders
            </h3>

            <Button
              to={OrderListPath}
              data-testid="dashboard-view-all-orders-button"
              variant="plain"
              size="sm"
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              View all orders
            </Button>
          </div>

          <LatestOrdersStats
            orders={orders.items}
            isLoading={isLoadingOrders}
            limit={LAST_ORDERS_LIMIT}
            isSupplier
            orderVariancesByOrderId={variancesByOrderId}
          />
        </div>
      </section>
    </div>
  );
};

export default Home;
